import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { 
  Plus, CheckCircle, XCircle, AlertTriangle, Search, Calendar,
  Truck, User, ClipboardCheck, PenTool, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import SignatureCanvas from 'react-signature-canvas';

const Checklists = () => {
  const { user } = useContext(AuthContext);
  const [checklists, setChecklists] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [filter, setFilter] = useState('all');
  const signatureRef = useRef(null);
  const [formData, setFormData] = useState({
    machinery_id: '',
    date: new Date().toISOString().split('T')[0],
    horometer_reading: '',
    items: [],
    general_observations: '',
    operator_signature: ''
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const [checklistsRes, machineryRes, templateRes] = await Promise.all([
        axios.get(`${API}/checklists${params}`),
        axios.get(`${API}/machinery`),
        axios.get(`${API}/checklists/template`)
      ]);
      setChecklists(checklistsRes.data);
      setMachinery(machineryRes.data);
      setTemplate(templateRes.data.items);
      
      // Initialize form items from template
      if (templateRes.data.items.length > 0) {
        setFormData(prev => ({
          ...prev,
          items: templateRes.data.items.map(item => ({
            item: item.item,
            category: item.category,
            status: 'ok',
            notes: ''
          }))
        }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get signature if available
    let signature = formData.operator_signature;
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      signature = signatureRef.current.toDataURL();
    }
    
    try {
      await axios.post(`${API}/checklists`, {
        ...formData,
        horometer_reading: formData.horometer_reading ? parseFloat(formData.horometer_reading) : null,
        operator_signature: signature
      });
      toast.success('Checklist registrado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };
const reviewChecklist = async (checklistId, status) => {
  try {
    let review_notes = "";

    if (status === "rejected") {
      review_notes = window.prompt("Ingrese el motivo del rechazo:");

      if (review_notes === null) return;

      if (!review_notes.trim()) {
        toast.error("Debe ingresar una observación.");
        return;
      }
    }

    await axios.patch(`${API}/checklists/${checklistId}/status`, {
      status,
      review_notes
    });

    toast.success(
      status === "approved"
        ? "Checklist aprobado."
        : "Checklist rechazado."
    );

    fetchData();

    if (detailDialog?.id === checklistId) {
      setDetailDialog(null);
    }

  } catch (error) {
    toast.error(
      error.response?.data?.detail || "Error al revisar el checklist."
    );
  }
};
  function resetForm() {
    setFormData({
      machinery_id: '',
      date: new Date().toISOString().split('T')[0],
      horometer_reading: '',
      items: template.map(item => ({
        item: item.item,
        category: item.category,
        status: 'ok',
        notes: ''
      })),
      general_observations: '',
      operator_signature: ''
    });
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  }

  const getStatusBadge = (status) => {
    const config = {
      approved: { color: 'bg-green-100 text-green-700', label: 'Aprobado', icon: CheckCircle },
      pending_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Revisión Pendiente', icon: AlertTriangle },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rechazado', icon: XCircle }
    };
    const { color, label, icon: Icon } = config[status] || config.pending_review;
    return (
      <span className={`status-badge ${color} flex items-center`}>
        <Icon size={14} className="mr-1" />
        {label}
      </span>
    );
  };

  const getItemStatusIcon = (status) => {
    switch (status) {
      case 'ok': return <CheckCircle size={18} className="text-green-500" />;
      case 'warning': return <AlertTriangle size={18} className="text-yellow-500" />;
      case 'fail': return <XCircle size={18} className="text-red-500" />;
      default: return <span className="text-slate-400">N/A</span>;
    }
  };

  // Group items by category
  const groupedItems = formData.items.reduce((acc, item, index) => {
    const category = item.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ ...item, index });
    return acc;
  }, {});

  return (
    <div data-testid="checklists-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Checklist Pre-Operación
          </h1>
          <p className="text-slate-600 mt-2">Inspección diaria antes de operar maquinaria</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-checklist-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Nuevo Checklist
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Checklist Pre-Operación
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Maquinaria *</label>
                  <select
                    value={formData.machinery_id}
                    onChange={(e) => setFormData({ ...formData, machinery_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {machinery.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Fecha *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Horómetro</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.horometer_reading}
                    onChange={(e) => setFormData({ ...formData, horometer_reading: e.target.value })}
                    className="input-field"
                    placeholder="Ej: 1234.5"
                  />
                </div>
              </div>

              {/* Checklist Items by Category */}
              <div className="space-y-4">
                {Object.entries(groupedItems).map(([category, items]) => (
                  <div key={category} className="border border-slate-200 rounded-md overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 font-semibold text-slate-700">{category}</div>
                    <div className="divide-y divide-slate-100">
                      {items.map((item) => (
                        <div key={item.index} className="px-4 py-3 flex items-center space-x-4">
                          <div className="flex-1">
                            <span className="text-sm text-slate-700">{item.item}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {['ok', 'warning', 'fail', 'na'].map((status) => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => handleItemChange(item.index, 'status', status)}
                                className={`p-2 rounded transition-colors ${
                                  formData.items[item.index].status === status
                                    ? status === 'ok' ? 'bg-green-100' :
                                      status === 'warning' ? 'bg-yellow-100' :
                                      status === 'fail' ? 'bg-red-100' : 'bg-slate-100'
                                    : 'hover:bg-slate-50'
                                }`}
                              >
                                {status === 'ok' && <CheckCircle size={20} className={formData.items[item.index].status === status ? 'text-green-600' : 'text-slate-300'} />}
                                {status === 'warning' && <AlertTriangle size={20} className={formData.items[item.index].status === status ? 'text-yellow-600' : 'text-slate-300'} />}
                                {status === 'fail' && <XCircle size={20} className={formData.items[item.index].status === status ? 'text-red-600' : 'text-slate-300'} />}
                                {status === 'na' && <span className={`text-xs font-medium ${formData.items[item.index].status === status ? 'text-slate-600' : 'text-slate-300'}`}>N/A</span>}
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="Nota..."
                            value={formData.items[item.index].notes || ''}
                            onChange={(e) => handleItemChange(item.index, 'notes', e.target.value)}
                            className="input-field w-32 text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Observations */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Observaciones Generales</label>
                <textarea
                  value={formData.general_observations}
                  onChange={(e) => setFormData({ ...formData, general_observations: e.target.value })}
                  className="input-field"
                  rows={2}
                  placeholder="Observaciones adicionales..."
                />
              </div>

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <PenTool size={16} className="inline mr-1" />
                  Firma del Operador *
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-md">
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    canvasProps={{
                      width: 500,
                      height: 150,
                      className: 'signature-canvas w-full'
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => signatureRef.current?.clear()}
                  className="text-sm text-orange-500 mt-1 hover:underline"
                >
                  Limpiar firma
                </button>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" onClick={() => { setDialogOpen(false); resetForm(); }} className="btn-secondary">
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary">
                  Registrar Checklist
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex space-x-2 mb-6">
        {['all', 'approved', 'pending_review', 'rejected'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-orange-500 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {f === 'all' ? 'Todos' : 
             f === 'approved' ? 'Aprobados' : 
             f === 'pending_review' ? 'Pendientes' : 'Rechazados'}
          </button>
        ))}
      </div>

      {/* Checklists List */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="space-y-4">
          {checklists.map((checklist) => (
            <div
              key={checklist.id}
              className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setDetailDialog(checklist)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-md flex items-center justify-center">
                    <ClipboardCheck size={24} className="text-orange-600" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-3">
                      <span className="font-semibold text-slate-800">{checklist.machinery_name}</span>
                      {getStatusBadge(checklist.status)}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-slate-600 mt-1">
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {new Date(checklist.date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center">
                        <User size={14} className="mr-1" />
                        {checklist.operator_name}
                      </span>
                      {checklist.horometer_reading && (
                        <span className="mono">Horómetro: {checklist.horometer_reading}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">

  {checklist.status === "pending_review" && (
    <>
      <Button
        size="sm"
        className="bg-green-600 hover:bg-green-700"
        onClick={(e) => {
          e.stopPropagation();
          reviewChecklist(checklist.id, "approved");
        }}
      >
        Aprobar
      </Button>

      <Button
        size="sm"
        variant="destructive"
        onClick={(e) => {
          e.stopPropagation();
          reviewChecklist(checklist.id, "rejected");
        }}
      >
        Rechazar
      </Button>
    </>
  )}

  <Eye size={20} className="text-slate-400" />

</div>
              </div>
            </div>
          ))}

          {checklists.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <ClipboardCheck size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No hay checklists registrados</p>
            </div>
          )}
        </div>
      )}

      {/* Detail Dialog */}
      {detailDialog && (
        <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle del Checklist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Maquinaria</p>
                  <p className="font-medium">{detailDialog.machinery_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Operador</p>
                  <p className="font-medium">{detailDialog.operator_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha</p>
                  <p className="font-medium">{new Date(detailDialog.date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Horómetro</p>
                  <p className="font-medium mono">{detailDialog.horometer_reading || '-'}</p>
                </div>
              </div>

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Item</th>
                      <th className="px-4 py-2 text-center w-20">Estado</th>
                      <th className="px-4 py-2 text-left">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailDialog.items?.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-4 py-2">{item.item}</td>
                        <td className="px-4 py-2 text-center">{getItemStatusIcon(item.status)}</td>
                        <td className="px-4 py-2 text-slate-600">{item.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detailDialog.general_observations && (
                <div>
                  <p className="text-sm text-slate-600">Observaciones</p>
                  <p className="bg-slate-50 p-3 rounded">{detailDialog.general_observations}</p>
                </div>
              )}

              {detailDialog.operator_signature && (
                <div>
                  <p className="text-sm text-slate-600 mb-2">Firma del Operador</p>
                  <img src={detailDialog.operator_signature} alt="Firma" className="border border-slate-200 rounded max-h-24" />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Checklists;
